import { Component, OnInit, OnDestroy, OnChanges, SimpleChanges, Input, DoCheck,
         ChangeDetectionStrategy, ChangeDetectorRef, NgZone,
         AfterViewInit, ViewChildren, ViewChild, QueryList, Injector, ViewContainerRef, ElementRef, HostListener, HostBinding } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTabsModule } from '@angular/material/tabs';
import { CommonModule } from '@angular/common';
import { DynamicTemplateComponent } from '../dynamic-template/dynamic-template.component';
import { ComponentInputBase } from '../../models/ComponentInputBase.model';
import { ComponentService } from '../../services/component/component.service';
import { Subject, timer, of, BehaviorSubject, Observable, merge, Subscription, combineLatest } from 'rxjs';
import { takeUntil, take, filter, delay, switchMap, catchError, debounceTime, distinctUntilChanged, map, skip } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import * as TemplateActions from '../../ngrx/template/template.actions';
import * as fromTemplate from '../../ngrx/template/template.selectors';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { DynamicTabsContentComponent } from '../dynamic-tabs-content/dynamic-tabs-content.component';
import { TabService, TabState } from '../../services/tab/tab.service';
import { AuthService } from '../../services/auth/auth.service';
import { ComponentDataSourceService } from '../../services/component-data-source/component-data-source.service';
import { TemplateService } from '../../services/template/template.service';
import { LetDirective } from '@ngrx/component';

interface TabConfig {
  text: string;
  slug: string;
}

/** Tab added when its data source evaluates to true, merged on top of the matched rule's tabs. */
export interface AlwaysVisibleTabConfig {
  text: string;
  slug: string;
  dataSource: string;
}

/** Condition for a tab set rule (auth, optional roles, or data-source boolean). */
export interface TabSetCondition {
  auth: 'any' | 'logged-in' | 'not-logged-in' | 'data-source-true' | 'data-source-false';
  roles?: string[];
  /** Registered component-data-source key when auth is data-source-true/false. */
  dataSource?: string;
}

/** Single rule: condition + tabs; isDefault = use when no other rule matches. */
export interface TabSetRule {
  condition: TabSetCondition;
  tabs: TabConfig[];
  isDefault?: boolean;
}

/** Structured tabs config (version 1) with ordered rules. */
export interface TabsConfigRules {
  version: 1;
  rules: TabSetRule[];
  /** Tabs merged in when their dataSource returns true, regardless of matched rule. */
  alwaysVisibleTabs?: AlwaysVisibleTabConfig[];
}

// Create a static state holder to maintain tab state across component instances
const DynamicTabsState = {
  lastActiveTab: '',
  lastSelectedIndex: 0,
  isInitializing: false, // Add flag to prevent concurrent initializations
  initializationCount: 0 // Track how many times we've initialized
};

@Component({
  selector: 'app-dynamic-tabs',
  templateUrl: './dynamic-tabs.component.html',
  styleUrls: ['./dynamic-tabs.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatButtonModule,
    MatTabsModule,
    LetDirective
    //DynamicTabsContentComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // duna-full-bleed opts this component out of the global 14px side padding (styles.scss)
  // so the tab bar spans edge-to-edge. The CMS fullBleed setting can still override this.
  host: { class: 'duna-full-bleed' }
})
export class DynamicTabsComponent extends ComponentInputBase implements OnInit, OnDestroy, OnChanges, DoCheck, AfterViewInit {
  @Input() tabsConfig: TabConfig[] | string | TabsConfigRules = [];

  @Input()
  override set dynamicInput(inputValue: { [key: string]: { type: string; value: any } }) {
    const prevConfig = this.hasInput('tabsConfig') ? this.getInput('tabsConfig') : undefined;
    if (inputValue) {
      Object.keys(inputValue).forEach(key => {
        this.inputs[key] = this.componentService.getDynamicInputType(inputValue[key]);
      });
    }
    const nextConfig = this.hasInput('tabsConfig') ? this.getInput('tabsConfig') : undefined;
    if (!this.tabConfigValuesEqual(prevConfig, nextConfig)) {
      this.onTabsConfigChanged();
    }
  }
  
  tabs: TabConfig[] = [];
  activeTab: string = '';

  @ViewChild('tabLabelScroll') tabLabelScroll?: ElementRef<HTMLElement>;

  canScrollTabsLeft = false;
  canScrollTabsRight = false;

  private tabScrollObserver?: ResizeObserver;
  
  @HostBinding('class.auto-height-enabled') get autoHeightEnabled() {
    return this.detectAutoHeightContext();
  }
  
  // Component for individual tab rendering
  DynamicTabsContentComponent = DynamicTabsContentComponent;
  
  private destroy$ = new Subject<void>();
  private pendingChanges = false;
  private injectorCache = new Map<string, Injector>();
  private preventNextNavigationHandling = false; // Flag to prevent tab selection issues during navigation
  private isPostRefresh = true; // Set to true by default to force aggressive loading on initial render
  private contentInitialized = false; // Track whether content has been fully initialized
  private initialSelectedTabLoaded = false; // Flag to track if initial selected tab has been loaded
  private tabLoadTimeouts = new Map<string, any>(); // To track load timeouts for cleanup
  private lastTabSelectionTime = 0; // Timestamp of the last tab selection
  private tabSelectionThrottleTime = 300; // Minimum time between tab selections (ms)
  private activeTabLoadInProgress = false; // Flag to track if a tab load is in progress
  private processedLoadEvents = new Set<string>(); // Track which load events we've already processed
  private isLoadingAllTabs = false; // Flag to prevent multiple concurrent loadAllTabsContent calls
  private dataSourceConditionValues = new Map<string, boolean>();
  private dataSourceConditionReady = new Map<string, boolean>();
  private dataSourceConditionSubs = new Subscription();
  /** False until auth + data-source conditions for rules-based tabsConfig are resolved. */
  tabsDisplayReady = false;
  private authStateReady = false;
  private tabsInitialSetupDone = false;
  private lastTabsConfigSnapshot: string | null = null;

  // Use BehaviorSubject to track selectedIndex and ensure it persists through updates
  private selectedIndexSubject = new BehaviorSubject<number>(0);
  selectedIndex$ = this.selectedIndexSubject.asObservable();

  // Use BehaviorSubject to track tabs to ensure consistent rendering
  private tabsSubject = new BehaviorSubject<TabConfig[]>([]);
  tabs$ = this.tabsSubject.asObservable().pipe(
    distinctUntilChanged((prev, curr) => 
      prev.length === curr.length && 
      prev.every((tab, i) => tab.slug === curr[i].slug && tab.text === curr[i].text)
    )
  );
  
  // Private storage for selectedIndex
  private _selectedIndex = 0;
  
  // Getter for selectedIndex
  get selectedIndex(): number {
    return this._selectedIndex;
  }
  
  // Setter for selectedIndex with logging
  set selectedIndex(value: number) {
    this.logDebug(`selectedIndex setter called with: ${value}, previous: ${this._selectedIndex}`);
    this._selectedIndex = value;
  }

  constructor(
    private store: Store<any>,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private router: Router,
    private route: ActivatedRoute,
    private actions$: Actions,
    private injector: Injector,
    public tabService: TabService,
    public templateService: TemplateService,
    private authService: AuthService,
    private componentDataSourceService: ComponentDataSourceService,
    protected override componentService: ComponentService
  ) {
    super(componentService);
    
    // Update the router navigation handler
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
        debounceTime(50) // Debounce to handle rapid navigation events
      )
      .subscribe((event: any) => {
        this.logDebug('Navigation detected');
        
        // Skip if navigation handling is temporarily disabled
        if (this.preventNextNavigationHandling) {
          this.logDebug('Skipping navigation handling due to recent tab change');
          return;
        }
        
        // CRITICAL FIX: Check if this navigation was triggered by our tab component
        // by examining the navigation state
        const navigationState = event?.state || window.history.state;
        if (navigationState && navigationState.noReload === true && navigationState.source === 'tab-component') {
          this.logDebug('Ignoring navigation event that was triggered by our tab component');
          return;
        }
        
        // Get current path/slug from URL
        const currentSlug = this.getCurrentSlugFromUrl();
        
        this.logDebug(`Current URL after navigation: ${currentSlug}`);
        
        // First check if there's a matching tab for the current URL
        if (this.tabs && this.tabs.length > 0) {
          const tabSlugs = this.tabs.map(tab => tab.slug);
          const matchingTabSlug = this.tabService.getMatchingTabSlug(currentSlug, tabSlugs);
          
          if (matchingTabSlug) {
            this.logDebug(`Found matching tab for navigated URL: ${matchingTabSlug}`);
            const matchingTabIndex = this.tabs.findIndex(tab => tab.slug === matchingTabSlug);
            
            if (matchingTabIndex >= 0 && matchingTabSlug !== this.activeTab) {
              // Select the matching tab programmatically
              this.activeTab = matchingTabSlug;
              this.selectedIndex = matchingTabIndex;
              this.selectedIndexSubject.next(matchingTabIndex);
              
              // Store in static state
              DynamicTabsState.lastActiveTab = matchingTabSlug;
              DynamicTabsState.lastSelectedIndex = matchingTabIndex;
              
              // Ensure tab content is loaded - force load here since it's from navigation
              this.forceLoadTabContent(matchingTabSlug);
              
      // REDUCED CHANGE DETECTION: Prevent excessive cycles
      this.markForCheck();
              
              return; // Skip the updateTabsWithoutDestruction call
            }
          }
        }
        
        // If no matching tab found, proceed with normal tab update
        // Store current tab info without recreating the component
        const currentTab = this.activeTab;
        
        // Update tabs configuration without destroying existing tabs
        this.updateTabsWithoutDestruction(currentTab);
      });
      
    // Listen for successful data loads
    this.actions$.pipe(
      ofType(TemplateActions.loadPageDataSuccess, TemplateActions.loadPageDataUnchanged),
      takeUntil(this.destroy$)
    ).subscribe(action => {
      // Forward action to tab service
      const slug = action.slug;
      
      // Skip if we're currently initializing to prevent loops
      if (DynamicTabsState.isInitializing) {
        return;
      }
      
      // Create a unique key for this load event to prevent duplicate processing
      const loadEventKey = `${slug}-${Date.now()}`;
      
      // Check if we've already processed a recent load event for this slug
      const recentEvents = Array.from(this.processedLoadEvents).filter(key => key.startsWith(slug));
      if (recentEvents.length > 0) {
        // If we processed this slug within the last 100ms, skip it
        const lastEventTime = Math.max(...recentEvents.map(key => parseInt(key.split('-')[1])));
        if (Date.now() - lastEventTime < 100) {
          return; // Skip duplicate processing
        }
      }
      
      this.processedLoadEvents.add(loadEventKey);
      
      // Clean up old processed events (keep only last 10 seconds worth)
      const cutoffTime = Date.now() - 10000;
      this.processedLoadEvents.forEach(key => {
        const eventTime = parseInt(key.split('-')[1]);
        if (eventTime < cutoffTime) {
          this.processedLoadEvents.delete(key);
        }
      });
      
      this.tabService.updateTabState(slug, { loading: false, failed: false, loaded: true });
      
      // Check if this is for the active tab and force render if needed
      if (slug === this.activeTab && !this.initialSelectedTabLoaded) {
        this.initialSelectedTabLoaded = true;
        this.ngZone.run(() => {
          this.cdr.detectChanges();
        });
      }
    });
    
    // Listen for load failures
    this.actions$.pipe(
      ofType(TemplateActions.loadPageDataFailure),
      takeUntil(this.destroy$)
    ).subscribe(action => {
      const slug = action.error?.slug;
      if (slug) {
        this.tabService.updateTabState(slug, { loading: false, failed: true, loaded: false });
      }
    });

    // Re-resolve tab set when auth state or roles change (for conditional tab sets)
    combineLatest([
      this.authService.isAuthenticated$,
      this.authService.getRoles()
    ]).pipe(
      takeUntil(this.destroy$),
      debounceTime(50)
    ).subscribe(() => {
      if (this.getTabsConfigRules()) {
        this.tryPublishResolvedTabs(this.activeTab);
      }
    });

    this.authService.authStateReady$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((ready) => {
      this.authStateReady = ready;
      if (ready) {
        this.refreshDataSourceConditionSubscriptions();
      }
    });

    this.templateService.currentMode$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.ngZone.run(() => {
        this.cdr.markForCheck();
        setTimeout(() => this.setupCustomTabHeader(), 0);
      });
    });
  }

  ngOnInit(): void {
    
    // CRITICAL: Prevent multiple concurrent initializations
    if (DynamicTabsState.isInitializing) {
      return;
    }
    
    DynamicTabsState.isInitializing = true;
    DynamicTabsState.initializationCount++;
    
    
    // CRITICAL FIX: Check if this initialization was caused by a tab URL update
    const navigationState = window.history.state;
    if (navigationState && navigationState.noReload === true && navigationState.source === 'tab-component') {
      
      // If we have data in the static state holder, use it instead of reinitializing
      if (DynamicTabsState.lastActiveTab) {
        
        // Parse tabs but maintain the previous tab selection
        this.tabsDisplayReady = true;
        this.tabsInitialSetupDone = true;
        this.tabs = this.resolveTabsFromConfig();
        this.tabsSubject.next(this.tabs);
        
        if (this.tabs.some(tab => tab.slug === DynamicTabsState.lastActiveTab)) {
          // Use the previous selection
          this.activeTab = DynamicTabsState.lastActiveTab;
          this.selectedIndex = DynamicTabsState.lastSelectedIndex;
          this.selectedIndexSubject.next(DynamicTabsState.lastSelectedIndex);
          
          // Force a render cycle
          this.ngZone.run(() => {
            this.cdr.detectChanges();
          });
          
          // DON'T load content again - it should already be loaded
          this.initialSelectedTabLoaded = true;
          
          // Force a change detection to ensure the UI is updated
          setTimeout(() => {
            this.cdr.detectChanges();
            DynamicTabsState.isInitializing = false; // Reset flag
          }, 0);
          
          return; // Skip the normal initialization
        }
      }
    }
    
    this.refreshDataSourceConditionSubscriptions();
  }
  
  ngAfterViewInit(): void {
    this.injectorCache.clear();

    if (this.detectAutoHeightContext()) {
      this.ngZone.run(() => {
        this.cdr.detectChanges();
        setTimeout(() => {
          this.applyAutoHeightStyles();
          this.requestScalingUpdate();
        }, 100);
      });
    }

    if (!this.initialSelectedTabLoaded && this.activeTab) {
      this.forceLoadTabContent(this.activeTab);
      this.contentInitialized = true;
    }

    this.setupCustomTabHeader();
  }

  private setupCustomTabHeader(): void {
    this.updateTabScrollState();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const scrollEl = this.tabLabelScroll?.nativeElement;
    if (!scrollEl) {
      setTimeout(() => this.setupCustomTabHeader(), 100);
      return;
    }

    this.tabScrollObserver?.disconnect();
    this.tabScrollObserver = new ResizeObserver(() => {
      this.ngZone.run(() => this.updateTabScrollState());
    });
    this.tabScrollObserver.observe(scrollEl);
  }

  updateTabScrollState(): void {
    const el = this.tabLabelScroll?.nativeElement;
    if (!el) {
      return;
    }

    const maxScroll = el.scrollWidth - el.clientWidth;
    this.canScrollTabsLeft = el.scrollLeft > 2;
    this.canScrollTabsRight = maxScroll - el.scrollLeft > 2;
    this.cdr.markForCheck();
  }

  scrollTabs(direction: -1 | 1): void {
    const el = this.tabLabelScroll?.nativeElement;
    if (!el) {
      return;
    }

    el.scrollBy({
      left: direction * Math.max(120, el.clientWidth * 0.6),
      behavior: 'smooth'
    });
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateTabScrollState();
  }

  private scrollActiveTabIntoView(): void {
    requestAnimationFrame(() => {
      const scrollEl = this.tabLabelScroll?.nativeElement;
      if (!scrollEl) {
        return;
      }

      const activeButton = scrollEl.querySelector('.duna-tab-button--active') as HTMLElement | null;
      activeButton?.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'smooth' });
      this.updateTabScrollState();
    });
  }

  /** Override Material inline styles when auto-height is enabled. */
  private applyAutoHeightStyles(): void {
    try {
      const elementRef = this.injector.get(ElementRef, null);
      if (!elementRef?.nativeElement) {
        return;
      }

      const hostElement = elementRef.nativeElement;

      if (!hostElement.classList.contains('auto-height-enabled')) {
        hostElement.classList.add('auto-height-enabled');
      }

      hostElement.style.height = 'auto';
      hostElement.style.minHeight = 'auto';
      hostElement.style.maxHeight = 'none';
      hostElement.style.overflow = 'visible';

      const isHeaderElement = (element: Element): boolean =>
        element.closest('.mat-mdc-tab-header') !== null;

      const isInactiveTabBody = (element: Element): boolean =>
        element.classList.contains('mat-mdc-tab-body')
        && !element.classList.contains('mat-mdc-tab-body-active');

      const bodyContentSelectors = [
        '.mat-mdc-tab-body-active .mat-mdc-tab-body-content',
        'app-dynamic-tabs-content'
      ];

      bodyContentSelectors.forEach((selector) => {
        hostElement.querySelectorAll(selector).forEach((element: Element) => {
          if (isHeaderElement(element) || isInactiveTabBody(element)) {
            return;
          }

          const htmlElement = element as HTMLElement;
          htmlElement.style.setProperty('height', 'auto', 'important');
          htmlElement.style.setProperty('min-height', 'auto', 'important');
          htmlElement.style.setProperty('max-height', 'none', 'important');
          htmlElement.style.setProperty('overflow', 'visible', 'important');
          htmlElement.style.setProperty('overflow-y', 'visible', 'important');
        });
      });
    } catch (error) {
      console.error('[DynamicTabs] Error forcing auto-height styles:', error);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tabsConfig']) {
      this.onTabsConfigChanged();
    }
  }

  ngDoCheck(): void {
    const snapshot = this.getTabsConfigSnapshot();
    if (snapshot === this.lastTabsConfigSnapshot) {
      return;
    }
    this.lastTabsConfigSnapshot = snapshot;
    if (snapshot !== null) {
      this.onTabsConfigChanged();
    }
  }

  private getTabsConfigSnapshot(): string | null {
    const raw = this.getRawTabsConfig();
    if (raw == null) {
      return null;
    }
    try {
      return JSON.stringify(raw);
    } catch {
      return String(raw);
    }
  }

  private onTabsConfigChanged(): void {
    const snapshot = this.getTabsConfigSnapshot();
    if (snapshot !== null) {
      this.lastTabsConfigSnapshot = snapshot;
    }
    this.tabsInitialSetupDone = false;
    this.refreshDataSourceConditionSubscriptions();
  }

  private tabConfigValuesEqual(a: unknown, b: unknown): boolean {
    if (a === b) return true;
    if (a == null || b == null) return false;
    if (typeof a === 'string' && typeof b === 'string') {
      return a.trim() === b.trim();
    }
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch {
      return false;
    }
  }

  /** Raw tabsConfig from dynamicInput or @Input — may arrive after ngOnInit. */
  private getRawTabsConfig(): unknown {
    if (this.hasInput('tabsConfig')) {
      const fromInput = this.getInput('tabsConfig');
      if (fromInput !== undefined && fromInput !== null && fromInput !== '') {
        return fromInput;
      }
    }
    const fromProp = this.tabsConfig;
    if (fromProp === undefined || fromProp === null || fromProp === '') {
      return null;
    }
    if (Array.isArray(fromProp) && fromProp.length === 0) {
      return null;
    }
    return fromProp;
  }

  ngOnDestroy(): void {
    
    // Reset static initialization flag
    DynamicTabsState.isInitializing = false;

    this.tabScrollObserver?.disconnect();
    this.tabScrollObserver = undefined;
    
    // Don't call stopAllTabLoading - it's causing unnecessary component destruction
    // Instead, only clean up timeouts and subjects
    
    // Clear injector cache
    this.injectorCache.clear();
    
    // Clean up any pending timeouts
    this.tabLoadTimeouts.forEach((timeout) => {
      clearTimeout(timeout);
    });
    this.tabLoadTimeouts.clear();
    
    // Clear processed load events
    this.processedLoadEvents.clear();
    
    // Complete subjects
    this.dataSourceConditionSubs.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
    this.selectedIndexSubject.complete();
    this.tabsSubject.complete();
    
  }
  
  // Create an injector with tab-specific data
  getTabInjector(slug: string): Injector {
    // Check if we already have an injector for this tab
    if (this.injectorCache.has(slug)) {
      return this.injectorCache.get(slug)!;
    }
    
    // Only create a new injector if we don't already have one
    
    // Detect if we're in an auto-height context by checking parent elements
    const isAutoHeight = this.detectAutoHeightContext();
    
    // Set up providers for the tab
    const isActive = this.activeTab === slug;
    const providers = [
      { provide: 'TAB_SLUG', useValue: slug },
      { provide: 'IS_ACTIVE_TAB', useValue: isActive },
      { provide: 'AUTO_HEIGHT_ENABLED', useValue: isAutoHeight }
    ];
    
    
    // Create a new injector
    const newInjector = Injector.create({
      providers,
      parent: this.injector
    });
    
    // Cache the injector
    this.injectorCache.set(slug, newInjector);
    return newInjector;
  }
  
  // Method to update injector properties without creating a new one
  updateInjectorActiveState(slug: string, isActive: boolean): void {
    if (!this.injectorCache.has(slug)) {
      return;
    }
    
    // Detect current auto-height context
    const isAutoHeight = this.detectAutoHeightContext();
    
    // Create a new injector with updated values
    const providers = [
      { provide: 'TAB_SLUG', useValue: slug },
      { provide: 'IS_ACTIVE_TAB', useValue: isActive },
      { provide: 'AUTO_HEIGHT_ENABLED', useValue: isAutoHeight }
    ];
    
    // Create updated injector and replace the old one
    const updatedInjector = Injector.create({
      providers,
      parent: this.injector
    });
    
    this.injectorCache.set(slug, updatedInjector);
  }
  
  // Detect if this tabs component is inside an auto-height grid item
  private detectAutoHeightContext(): boolean {
    try {
      const elementRef = this.injector.get(ElementRef, null);
      if (!elementRef?.nativeElement) {
        return false;
      }

      let current = elementRef.nativeElement.parentElement;

      while (current) {
        if (
          current.classList?.contains('grid-item')
          && current.getAttribute('data-auto-height') === 'true'
        ) {
          elementRef.nativeElement.classList.add('auto-height-enabled');
          return true;
        }

        if (current.id === 'main' || current.classList?.contains('scalable-grid-item-host')) {
          break;
        }

        current = current.parentElement;
      }

      elementRef.nativeElement.classList.remove('auto-height-enabled');
      return false;
    } catch (error) {
      console.warn('[DynamicTabs] Error detecting auto-height context:', error);
      return false;
    }
  }

  // Safely trigger change detection
  private markForCheck(): void {
    this.ngZone.run(() => {
      setTimeout(() => {
        this.cdr.markForCheck();
      });
    });
  }

  // Creating a consistent logging function for tracking tab issues
  private logDebug(message: string, data?: any): void {
    // TEMPORARILY ENABLED to verify the fix
    const prefix = '[TAB-DEBUG]';
    if (data) {
    } else {
    }
  }

  private requestScalingUpdate(): void {
    if (typeof document === 'undefined') {
      return;
    }

    requestAnimationFrame(() => {
      document.dispatchEvent(new CustomEvent('duna-request-scaling-update'));
    });
  }

  // Replace forceLoadTabContent with a simpler, more direct approach
  private directLoadTabContent(slug: string): void {
    if (!slug) return;
    
    // SIMPLE FIX: Check if store has the COMPLETE data needed for DynamicTemplateComponent
    this.store.select(fromTemplate.selectSlugData(slug))
      .pipe(take(1))
      .subscribe(existingData => {
        // Check if we have the complete data structure that DynamicTemplateComponent needs
        const hasCompleteData = existingData && existingData.loaded && existingData.customSlug;
        
        if (!hasCompleteData) {
          setTimeout(() => {
            this.directLoadTabContent(slug);
          }, 500);
          return;
        }
        
        // Complete data exists, proceed with loading
        this.performTabLoad(slug);
      });
  }

  // Separated the actual loading logic
  private performTabLoad(slug: string): void {
    // Check if we already have a pending load for this tab
    if (this.tabLoadTimeouts.has(slug)) {
      return;
    }
    
    // Check if the tab is already loaded and we're not forcing a reload
    const currentState = this.tabService.getTabStateSubject(slug).getValue();
    if (currentState.loaded && !currentState.loading) {
      return;
    }
    
    
    // Use TabService ONLY to avoid conflicting state updates
    // TabService will handle setting loading state internally
    this.tabService.loadTabContent(slug, !currentState.loaded); // Only force reload if not loaded
    
    // Force a render cycle to show loading state
    this.ngZone.run(() => {
      this.cdr.detectChanges();
    });
    
    // Set a timeout to clear our local timeout tracking
    const timeout = setTimeout(() => {
      this.tabLoadTimeouts.delete(slug);
    }, 1000); // Just for local cleanup tracking
    
    this.tabLoadTimeouts.set(slug, timeout);
  }

  // Direct URL update that avoids navigation issues
  private directUpdateUrlForTab(tabSlug: string): void {
    if (!tabSlug.startsWith('tab/')) {
      return;
    }
    
    // Get the slug without the 'tab/' prefix
    const pageSlug = tabSlug.substring(4); // 'tab/'.length = 4
    
    // Update URL without navigation
    const url = `/${pageSlug}`;
    
    // Don't update if the URL is already correct
    if (window.location.pathname === url) {
      return;
    }
    
    // CRITICAL FIX: Use replaceState with a special flag in the state object
    // to indicate this is just a URL update without navigation
    window.history.replaceState(
      { noReload: true, source: 'tab-component' }, // State object with flag
      '', 
      url
    );
  }

  // Add this utility method for direct tab selection (for programmatic use)
  public selectTabBySlug(slug: string): void {
    if (!this.tabs || !slug) return;
    
    const tabIndex = this.tabs.findIndex(tab => tab.slug === slug);
    if (tabIndex === -1) return;
    
    
    // Update state variables
    this.activeTab = slug;
    this.selectedIndex = tabIndex;
    
    // Save to static state
    DynamicTabsState.lastActiveTab = slug;
    DynamicTabsState.lastSelectedIndex = tabIndex;
    
    // Update subject and trigger change detection
    this.selectedIndexSubject.next(tabIndex);
    
    // Force UI update
    this.ngZone.run(() => {
      this.cdr.detectChanges();
      
      // Force content loading
      this.directLoadTabContent(slug);
    });
  }

  // Helper method to update URL without navigation when tab changes
  private updateUrlForTab(tabSlug: string): void {
    if (!tabSlug.startsWith('tab/')) {
      return;
    }
    
    // Get the slug without the 'tab/' prefix
    const pageSlug = tabSlug.substring(4); // 'tab/'.length = 4
    
    // Update browser URL without triggering navigation
    const url = `/${pageSlug}`;
    const currentUrl = this.router.url.split('?')[0];
    
    
    if (currentUrl !== url) {
      // Use history.replaceState to update URL without navigation
      window.history.replaceState({}, '', url);
    }
  }

  /** Resolve which tab set to show from config (string, array, or rules object). */
  private resolveTabsFromConfig(): TabConfig[] {
    // Prefer inputs (set by dynamic-component) so we always use latest value
    let config: any = this.getInput ? this.getInput('tabsConfig') : undefined;
    if (config === undefined || config === null) {
      config = this.tabsConfig;
    }
    if (typeof config === 'string') {
      const tabsString = config.trim();
      if (!tabsString) return [];
      // If string looks like JSON (e.g. from API/storage), parse as rules
      if (tabsString.startsWith('{')) {
        try {
          config = JSON.parse(tabsString);
          // Fall through to object handling below
        } catch {
          // Legacy format: "Label,slug;Label2,slug2"
          return tabsString.split(';')
            .map(tabStr => {
              const [text, slug] = tabStr.split(',');
              return { text: (text || '').trim(), slug: (slug || '').trim() };
            })
            .filter(tab => tab.text && tab.slug);
        }
      } else {
        return tabsString.split(';')
          .map(tabStr => {
            const [text, slug] = tabStr.split(',');
            return { text: (text || '').trim(), slug: (slug || '').trim() };
          })
          .filter(tab => tab.text && tab.slug);
      }
    }
    if (Array.isArray(config)) {
      return [...config];
    }
    const rulesConfig = config as TabsConfigRules;
    if (rulesConfig && rulesConfig.rules && Array.isArray(rulesConfig.rules)) {
      const isAuthenticated = this.authService.isAuthenticated;
      let userRoles = this.authService.getUserRoles();
      if (!userRoles || userRoles.length === 0) {
        const user = this.authService.getCurrentUser();
        userRoles = (user && user.roles && Array.isArray(user.roles)) ? user.roles : [];
      }
      const P = '[DynamicTabs Rules]';
      const defaultRule = rulesConfig.rules.find(r => r.isDefault);
      const nonDefaultRules = rulesConfig.rules.filter(r => !r.isDefault);
      for (let i = 0; i < nonDefaultRules.length; i++) {
        const rule = nonDefaultRules[i];
        const matched = this.ruleMatches(rule.condition, isAuthenticated, userRoles);
        if (matched) {
          const ruleTabs = rule.tabs && rule.tabs.length ? [...rule.tabs] : [];
          return this.mergeAlwaysVisibleTabs(ruleTabs, rulesConfig);
        }
      }
      const defaultTabs = defaultRule?.tabs?.length ? [...defaultRule.tabs] : [];
      return this.mergeAlwaysVisibleTabs(defaultTabs, rulesConfig);
    }
    return [];
  }

  private mergeAlwaysVisibleTabs(baseTabs: TabConfig[], rulesConfig: TabsConfigRules): TabConfig[] {
    const alwaysVisible = Array.isArray(rulesConfig.alwaysVisibleTabs)
      ? rulesConfig.alwaysVisibleTabs
      : [];
    if (alwaysVisible.length === 0) {
      return baseTabs;
    }

    const merged = [...baseTabs];
    const existingSlugs = new Set(merged.map(t => t.slug));

    for (const entry of alwaysVisible) {
      const dsKey = entry.dataSource?.trim();
      if (!dsKey || !entry.text?.trim() || !entry.slug?.trim()) continue;
      if (!this.dataSourceConditionReady.get(dsKey)) continue;
      if (!(this.dataSourceConditionValues.get(dsKey) ?? false)) continue;
      if (existingSlugs.has(entry.slug)) continue;
      merged.push({ text: entry.text.trim(), slug: entry.slug.trim() });
      existingSlugs.add(entry.slug);
    }

    return merged;
  }

  private ruleMatches(condition: TabSetCondition, isAuthenticated: boolean, userRoles: string[]): boolean {
    if (!condition) return false;
    if (condition.auth === 'data-source-true' || condition.auth === 'data-source-false') {
      const dsKey = condition.dataSource?.trim();
      if (!dsKey) return false;
      if (!this.dataSourceConditionReady.get(dsKey)) return false;
      const value = this.dataSourceConditionValues.get(dsKey) ?? false;
      return condition.auth === 'data-source-true' ? value : !value;
    }
    if (condition.auth === 'logged-in' && !isAuthenticated) return false;
    if (condition.auth === 'not-logged-in' && isAuthenticated) return false;
    const allowedRoles = Array.isArray(condition.roles) ? condition.roles : [];
    if (allowedRoles.length > 0) {
      const allowedLower = allowedRoles.map(r => String(r).toLowerCase());
      const roles = Array.isArray(userRoles) ? userRoles : [];
      const hasRole = roles.some(r => allowedLower.includes(String(r).toLowerCase()));
      if (!hasRole) return false;
    }
    return true;
  }

  private getTabsConfigRules(): TabsConfigRules | null {
    let config: any = this.getInput ? this.getInput('tabsConfig') : undefined;
    if (config === undefined || config === null) {
      config = this.tabsConfig;
    }
    if (typeof config === 'string') {
      const tabsString = config.trim();
      if (!tabsString.startsWith('{')) return null;
      try {
        config = JSON.parse(tabsString);
      } catch {
        return null;
      }
    }
    if (config && typeof config === 'object' && !Array.isArray(config) && Array.isArray(config.rules)) {
      return config as TabsConfigRules;
    }
    return null;
  }

  private collectRequiredDataSources(rulesConfig: TabsConfigRules): Set<string> {
    const dataSources = new Set<string>();
    for (const rule of rulesConfig.rules) {
      const auth = rule.condition?.auth;
      const dsKey = rule.condition?.dataSource?.trim();
      if ((auth === 'data-source-true' || auth === 'data-source-false') && dsKey) {
        dataSources.add(dsKey);
      }
    }
    const alwaysVisible = Array.isArray(rulesConfig.alwaysVisibleTabs)
      ? rulesConfig.alwaysVisibleTabs
      : [];
    for (const entry of alwaysVisible) {
      const dsKey = entry.dataSource?.trim();
      if (dsKey) {
        dataSources.add(dsKey);
      }
    }
    return dataSources;
  }

  private evaluateTabsDisplayReady(): boolean {
    const rawConfig = this.getRawTabsConfig();
    if (rawConfig == null || rawConfig === '') {
      return false;
    }
    if (!this.getTabsConfigRules()) {
      return true;
    }
    if (!this.authStateReady) {
      return false;
    }
    const rulesConfig = this.getTabsConfigRules();
    if (!rulesConfig) {
      return true;
    }
    for (const dsKey of this.collectRequiredDataSources(rulesConfig)) {
      if (!this.dataSourceConditionReady.get(dsKey)) {
        return false;
      }
    }
    return true;
  }

  private tryPublishResolvedTabs(currentTab: string): void {
    const wasReady = this.tabsDisplayReady;
    this.tabsDisplayReady = this.evaluateTabsDisplayReady();

    const previousTabs = this.tabsSubject.value.length > 0
      ? [...this.tabsSubject.value]
      : [...this.tabs];
    this.tabs = this.resolveTabsFromConfig();

    if (!this.tabsDisplayReady) {
      this.markForCheck();
      return;
    }

    const tabsChanged =
      this.tabs.length !== previousTabs.length ||
      this.tabs.some((tab, i) => i >= previousTabs.length || tab.slug !== previousTabs[i].slug);

    if (!wasReady || tabsChanged) {
      this.tabsSubject.next(this.tabs);

      if (!this.tabsInitialSetupDone) {
        this.tabsInitialSetupDone = true;
        this.initializeTabSelection();
      } else if (tabsChanged) {
        this.updateActiveTabAfterTabChange(currentTab);
      }
    }

    if (!wasReady && this.tabsDisplayReady) {
      this.requestScalingUpdate();
      setTimeout(() => this.setupCustomTabHeader(), 0);
    }

    if (this.tabsInitialSetupDone && this.tabs.length === 0) {
      DynamicTabsState.isInitializing = false;
    }

    this.ngZone.run(() => {
      this.cdr.detectChanges();
    });
  }

  private initializeTabSelection(): void {
    if (this.tabs.length === 0) {
      DynamicTabsState.isInitializing = false;
      return;
    }

    const currentSlug = this.getCurrentSlugFromUrl();
    const tabSlugs = this.tabs.map(tab => tab.slug);

    let selectedTabIndex = 0;
    let selectedTabSlug = '';

    const matchingTabSlug = this.tabService.getMatchingTabSlug(currentSlug, tabSlugs);
    if (matchingTabSlug) {
      const matchingTabIndex = this.tabs.findIndex(tab => tab.slug === matchingTabSlug);
      if (matchingTabIndex >= 0) {
        selectedTabIndex = matchingTabIndex;
        selectedTabSlug = matchingTabSlug;
      }
    } else if (DynamicTabsState.lastActiveTab && this.tabs.some(tab => tab.slug === DynamicTabsState.lastActiveTab)) {
      selectedTabSlug = DynamicTabsState.lastActiveTab;
      selectedTabIndex = DynamicTabsState.lastSelectedIndex;
    } else {
      selectedTabSlug = this.tabs[0].slug;
      selectedTabIndex = 0;
    }

    const isAlreadyLoaded = this.tabService.checkLoaded(selectedTabSlug);
    if (isAlreadyLoaded) {
      this.initialSelectedTabLoaded = true;
    }

    this.activeTab = selectedTabSlug;
    this.selectedIndex = selectedTabIndex;
    this.selectedIndexSubject.next(selectedTabIndex);

    DynamicTabsState.lastActiveTab = selectedTabSlug;
    DynamicTabsState.lastSelectedIndex = selectedTabIndex;

    this.tabs.forEach(tab => {
      this.getTabInjector(tab.slug);
    });

    if (!isAlreadyLoaded) {
      this.tabService.loadTabContent(selectedTabSlug, false);
      this.initialSelectedTabLoaded = true;
    }

    setTimeout(() => {
      this.cdr.detectChanges();
      DynamicTabsState.isInitializing = false;
      this.setupCustomTabHeader();
    }, 0);
  }

  private updateActiveTabAfterTabChange(currentTab: string): void {
    if (this.tabs.length === 0) {
      return;
    }

    let newSelectedIndex = 0;
    const currentSlug = this.getCurrentSlugFromUrl();
    const tabSlugs = this.tabs.map(tab => tab.slug);
    const defaultTabSlug = this.tabService.getDefaultTabSlug(currentSlug, tabSlugs);

    if (defaultTabSlug) {
      const matchingTabIndex = this.tabs.findIndex(tab => tab.slug === defaultTabSlug);
      if (matchingTabIndex >= 0) {
        newSelectedIndex = matchingTabIndex;
      }
    } else if (currentTab) {
      const existingTabIndex = this.tabs.findIndex(tab => tab.slug === currentTab);
      if (existingTabIndex >= 0) {
        newSelectedIndex = existingTabIndex;
      }
    }

    this.activeTab = this.tabs[newSelectedIndex].slug;
    this.selectedIndex = newSelectedIndex;
    this.selectedIndexSubject.next(newSelectedIndex);
    this.forceLoadTabContent(this.activeTab);
  }

  private refreshDataSourceConditionSubscriptions(): void {
    this.dataSourceConditionSubs.unsubscribe();
    this.dataSourceConditionSubs = new Subscription();
    this.dataSourceConditionValues.clear();
    this.dataSourceConditionReady.clear();

    const rulesConfig = this.getTabsConfigRules();
    if (!rulesConfig) {
      this.tryPublishResolvedTabs(this.activeTab);
      return;
    }

    this.tabsDisplayReady = false;

    if (!this.authStateReady) {
      this.markForCheck();
      return;
    }

    const dataSources = this.collectRequiredDataSources(rulesConfig);
    if (dataSources.size === 0) {
      this.tryPublishResolvedTabs(this.activeTab);
      return;
    }

    for (const dsKey of dataSources) {
      const obs = this.componentDataSourceService.executeDataSource(dsKey);
      if (!obs) {
        this.dataSourceConditionReady.set(dsKey, true);
        this.dataSourceConditionValues.set(dsKey, false);
        continue;
      }

      const sub = obs.pipe(
        catchError(() => of(false))
      ).subscribe(result => {
        this.dataSourceConditionValues.set(dsKey, this.normalizeDataSourceBoolean(result));
        this.dataSourceConditionReady.set(dsKey, true);
        this.tryPublishResolvedTabs(this.activeTab);
      });
      this.dataSourceConditionSubs.add(sub);
    }

    this.tryPublishResolvedTabs(this.activeTab);
  }

  private normalizeDataSourceBoolean(result: unknown): boolean {
    if (typeof result === 'boolean') return result;
    if (result != null && typeof result === 'object' && 'visible' in result) {
      return (result as { visible?: boolean }).visible !== false;
    }
    return true;
  }

  // Special method to update tabs without destroying the component
  private updateTabsWithoutDestruction(currentTab: string): void {
    this.tryPublishResolvedTabs(currentTab);
  }

  // Original parseTabs method kept for compatibility
  parseTabs(): void {
    // Use the new method for consistency
    this.updateTabsWithoutDestruction(this.activeTab);
  }
  
  // Load active tab content immediately, delay others
  loadActiveTabContent(): void {
    if (!this.tabs || this.tabs.length === 0) return;
    
    
    // Only load active tab immediately
    if (this.activeTab) {
      this.forceLoadTabContent(this.activeTab);
    }
  }
  
  // Load all tabs content immediately - keep for compatibility
  loadAllTabsContent(): void {
    this.loadActiveTabContent();
    
    // Prevent multiple concurrent calls to this method
    if (this.isLoadingAllTabs) {
      return;
    }
    this.isLoadingAllTabs = true;
    
    // Lazily load other tabs after active tab is loaded
    setTimeout(() => {
      for (const tab of this.tabs) {
        if (tab.slug !== this.activeTab) {
          this.tabService.loadTabContent(tab.slug);
        }
      }
      this.isLoadingAllTabs = false; // Reset flag
    }, 500);
  }
  
  // These methods are kept for backward compatibility
  preloadNonActiveTabContents(): void {
    for (const tab of this.tabs) {
      if (tab.slug !== this.activeTab) {
        this.tabService.loadTabContent(tab.slug);
      }
    }
  }
  
  preloadTabContents(): void {
    this.loadAllTabsContent();
  }

  trackBySlug(index: number, tab: TabConfig): string {
    return tab.slug;
  }

  // Stop loading all tabs
  private stopAllTabLoading(): void {
    if (this.tabs.length === 0) return;
    
    // Only clean up tabs that are not the active tab
    for (const tab of this.tabs) {
      if (tab.slug !== this.activeTab) {
        this.tabService.cleanupSlug(tab.slug);
      }
    }
    
    // Clear timeouts
    this.tabLoadTimeouts.forEach((timeout, slug) => {
      if (slug !== this.activeTab) {
        clearTimeout(timeout);
      }
    });
  }

  // Add a helper method to get the current slug from the router
  private getCurrentSlugFromUrl(): string {
    const currentPath = this.router.url.split('?')[0];
    let currentSlug = currentPath.startsWith('/') ? currentPath.substring(1) : currentPath;
    
    // Log the original slug
    
    // Handle special cases for 'indstillinger' routes
    if (currentSlug.startsWith('indstillinger/')) {
    }
    
    return currentSlug;
  }

  // Add this new method for better URL management after page refresh
  private updateUrlForTabForceNavigation(tabSlug: string): void {
    if (!tabSlug.startsWith('tab/')) {
      return;
    }
    
    // Get the slug without the 'tab/' prefix
    const pageSlug = tabSlug.substring(4); // 'tab/'.length = 4
    
    // Get current URL path
    const currentPath = this.router.url.split('?')[0];
    const url = `/${pageSlug}`;
    
    
    // Use router navigation to fully reload the route
    // This ensures the page state is fully reset for the new tab
    this.preventNextNavigationHandling = true;
    this.router.navigate([url], { replaceUrl: true }).then(() => {
      setTimeout(() => {
        this.preventNextNavigationHandling = false;
      }, 100);
    });
  }

  // Modify the forceLoadTabContent method to handle refresh cases better
  private forceLoadTabContent(slug: string): void {
    if (!slug) return;
    
    // Use our improved direct loading method
    this.directLoadTabContent(slug);
  }

  // Add this method to directly select a tab by index
  selectTabDirectly(index: number): void {
    const now = Date.now();
    if (now - this.lastTabSelectionTime < 100) {
      return;
    }
    this.lastTabSelectionTime = now;

    if (!this.tabs || index < 0 || index >= this.tabs.length) {
      return;
    }

    const selectedTab = this.tabs[index];
    const previousTab = this.activeTab;

    if (selectedTab.slug === previousTab) {
      return;
    }

    this.performTabSelection(index, selectedTab, previousTab);
  }

  private performTabSelection(index: number, selectedTab: any, previousTab: string): void {
    
    // Update injectors for active state change
    if (previousTab) {
      this.updateInjectorActiveState(previousTab, false);
    }
    this.updateInjectorActiveState(selectedTab.slug, true);
    
    // Update internal state immediately
    this.activeTab = selectedTab.slug;
    this.selectedIndex = index;
    this.selectedIndexSubject.next(index);
    
    // Update static state
    DynamicTabsState.lastActiveTab = selectedTab.slug;
    DynamicTabsState.lastSelectedIndex = index;
    
    // Block navigation handling temporarily
    this.preventNextNavigationHandling = true;
    
    // Force UI update first to show tab selection
    this.ngZone.run(() => {
      this.cdr.detectChanges();
    });
    
    // CRITICAL: Re-apply scrollbar fix after tab change
    const isAutoHeight = this.detectAutoHeightContext();
    if (isAutoHeight) {
      setTimeout(() => this.applyAutoHeightStyles(), 100);
      this.requestScalingUpdate();
    }
    
    // Load content using TabService to avoid state conflicts
    this.tabService.loadTabContent(selectedTab.slug, false);
    
    // Update URL if needed
    if (selectedTab.slug.startsWith('tab/')) {
      this.directUpdateUrlForTab(selectedTab.slug);
    }
    
    // Re-enable navigation handling after a short delay
    setTimeout(() => {
      this.preventNextNavigationHandling = false;
    }, 200);

    this.scrollActiveTabIntoView();
  }
}
